import {
  trackEvent,
  getCookie,
  getLocale,
  getMarket,
  isHmBrand,
  consoleEvents,
  getTouchpoint,
  isNorthstarBrand,
  getLabelFromStorage,
} from '@pb/utils'

import { TRACKING_EVENTS, getVoucherTracking } from 'App/constants'
import { setCartItemPrices } from 'features/cart/cart-overview/duck/actions'
import {
  CHECKOUT_TRACKING_ERROR_TYPES,
  CHECKOUT_CATEGORY_TRACKING_DATA,
  CHECKOUT_TRACKING_SECTIONS,
  CHECKOUT_TRACKING_EVENTS,
} from 'features/checkout/constants'
import { trackEvent as trackCOEvent } from 'features/checkout/duck/operations'
import { setOrderTotals } from 'features/orderTotals/duck/actions'
import getErrorData from 'helpers/getErrorData'
import { ThunkResult } from 'redux/types'
import { ApiError } from 'types'

import { MESSAGE_CODE_TO_LABEL_KEY, VOUCHERS_LOCATION } from '../constants'
import { getExtendedOfferInfo } from '../helpers'
import { fetchVouchers, postVouchers, deleteVoucher, fetchConfiguredOffers } from '../requests'
import { HasVoucherCode, Offer, Voucher } from '../types'
import {
  deleteVouchersStart,
  deleteVouchersSuccess,
  deleteVouchersFail,
  getVouchersStart,
  getVouchersSuccess,
  getVouchersFail,
  postVouchersStart,
  postVouchersSuccess,
  postVouchersFail,
  getConfiguredOffersStart,
  getConfiguredOffersSuccess,
  getConfiguredOffersFail,
  clearErrors,
  storeDeletedVouchers,
  clearStoredDeletedVouchers,
  setLastAppliedUnavailableVoucher,
  setPostingVoucherWithCode,
} from './actions'
import { normalizeVouchersResponse } from './normalize'
import selectors from './selectors'

export const operationGetVouchers = (): ThunkResult<Promise<void>> => async (dispatch) => {
  dispatch(getVouchersStart())
  try {
    const offers = await fetchVouchers()
    dispatch(getVouchersSuccess(offers))
  } catch (error) {
    const errorData = getErrorData(error)
    dispatch(getVouchersFail(errorData))
  }
}

export const operationGetConfiguredOffers = (): ThunkResult<Promise<void>> => async (dispatch) => {
  dispatch(getConfiguredOffersStart())
  try {
    const configuredOffers = await fetchConfiguredOffers()
    dispatch(getConfiguredOffersSuccess(configuredOffers))
  } catch (error) {
    consoleEvents.error(error)
    dispatch(getConfiguredOffersFail())
  }
}

const cartTracking = (): object => {
  return {
    category_id: 'SHOPPING_BAG',
    category_path: 'Checkout/Shopping Bag',
    event_id: 'DISCOUNT',
    list_action: '',
    page_id: 'CART',
    page_type: 'cart',
    product_action: 'checkout',
    region_currency: getCookie('HMCORP_currency') || '',
    region_locale: getLocale('mixed') || '',
    region_market: getMarket() || '',
    session_touchpoint: getTouchpoint() || '',
  }
}

const checkoutTracking = (): object => {
  return {
    ...CHECKOUT_CATEGORY_TRACKING_DATA,
    error_type: CHECKOUT_TRACKING_ERROR_TYPES.system,
    error_function: CHECKOUT_TRACKING_SECTIONS.general,
  }
}

const trackPostDiscounts = <T extends HasVoucherCode>(
  discount: T,
  isCart: boolean,
  isDiscount?: boolean,
): void => {
  const cartTrackingData = isDiscount
    ? { ...TRACKING_EVENTS.addDiscountFromCart.data }
    : { ...TRACKING_EVENTS.addVoucherFromCart.data }
  trackEvent({
    ...(isCart
      ? {
          ...cartTrackingData,
          ...getVoucherTracking(discount.voucherCode),
          ...(!isHmBrand && { discount_id: [discount?.voucherCode || ''] }),
        }
      : {
          ...TRACKING_EVENTS.addDiscount.data,
        }),
  })
}

export const createCartDiscountErrorTracking = (code?: string): object => {
  const errorMessageLabelKey = MESSAGE_CODE_TO_LABEL_KEY[code ?? 1006]
  const errorMessage = errorMessageLabelKey
    ? getLabelFromStorage(errorMessageLabelKey)
    : 'discount.code.not.valid'

  return {
    ...(isHmBrand || isNorthstarBrand
      ? {
          category_id: 'SHOPPING_BAG',
          category_path: 'CART',
          error_area: 'Shopping bag',
          error_function: 'discount_code',
          error_message: errorMessage,
          error_type: 'FIELD',
          page_id: 'CART',
        }
      : cartTracking()),
  }
}

const trackPostDiscountsError = (error: ApiError, isCart: boolean, isCheckout: boolean): void =>
  trackEvent({
    ...TRACKING_EVENTS.invalidDiscount.data,
    event_label: error?.message,
    ...(isCart && createCartDiscountErrorTracking(error?.code)),
    ...(isCheckout && checkoutTracking()),
  })

const trackDeleteVouchers =
  (voucherCode: string, isCart?: boolean, isDiscount?: boolean): ThunkResult<void> =>
  (_, getState) => {
    const state = getState()
    const offers = selectors.getOffers(state)
    const isOffer = offers.find((offer: Offer): boolean => offer.voucherCode === voucherCode)

    if (isOffer) {
      trackEvent({
        ...(isCart
          ? {
              ...TRACKING_EVENTS.removeVoucherFromCart.data,
              ...getVoucherTracking(voucherCode),
            }
          : {
              ...TRACKING_EVENTS.removeVoucher.data,
              event_label: voucherCode,
            }),
      })
    }

    if (isDiscount) {
      trackEvent({
        ...(isCart && {
          ...TRACKING_EVENTS.removeDiscountFromCart.data,
          ...getVoucherTracking(voucherCode),
        }),
      })
    }
  }

export const operationDeleteVouchers =
  (voucherCode: string, location?: string, isDiscount?: boolean): ThunkResult<Promise<void>> =>
  async (dispatch) => {
    const isCart = location === VOUCHERS_LOCATION.cart
    dispatch(deleteVouchersStart())
    try {
      const deleteVouchersResponse = await deleteVoucher(voucherCode)
      const normalizedVouchersResponse = normalizeVouchersResponse(deleteVouchersResponse)

      dispatch(setOrderTotals(normalizedVouchersResponse.orderTotals))
      dispatch(deleteVouchersSuccess(normalizedVouchersResponse))
      dispatch(setCartItemPrices(normalizedVouchersResponse))
      dispatch(storeDeletedVouchers(voucherCode))
      dispatch(trackDeleteVouchers(voucherCode, isCart, isDiscount))
    } catch (error) {
      dispatch(deleteVouchersFail(getErrorData(error)))
    }
  }

export const operationPostVouchers =
  <T extends HasVoucherCode>(
    voucher: T,
    location?: string,
    isDiscount?: boolean,
  ): ThunkResult<Promise<void>> =>
  async (dispatch, getState) => {
    dispatch(postVouchersStart())
    dispatch(setPostingVoucherWithCode(voucher?.voucherCode || ''))
    const isCart = location === VOUCHERS_LOCATION.cart
    const isCheckout = location === VOUCHERS_LOCATION.checkout

    const state = getState()
    const offers = selectors.getOffers(state)

    try {
      const voucherResponse = await postVouchers([voucher])
      const normalizedVouchersResponse = normalizeVouchersResponse(voucherResponse)

      const unAppliedVoucher = normalizedVouchersResponse.unAppliedVouchers.find((unAppliedV) => {
        return normalizedVouchersResponse.appliedVouchers.find(
          (appliedV) => appliedV.voucherCode === unAppliedV.details,
        )
      })

      const nonApplicableVoucher = normalizedVouchersResponse.unAppliedVouchers.find(
        (unAppliedV) => {
          return Object.values(offers).find(
            (voucherOffer) => voucherOffer.voucherCode === unAppliedV.details,
          )
        },
      )

      if (unAppliedVoucher && unAppliedVoucher?.details) {
        await dispatch(operationDeleteVouchers(unAppliedVoucher.details))
        dispatch(setLastAppliedUnavailableVoucher(unAppliedVoucher))
        dispatch(setPostingVoucherWithCode(''))
      } else if (nonApplicableVoucher) {
        normalizedVouchersResponse.unAppliedVouchers = []
        dispatch(postVouchersSuccess(normalizedVouchersResponse))
        dispatch(setLastAppliedUnavailableVoucher(nonApplicableVoucher))
        dispatch(setPostingVoucherWithCode(''))
      } else {
        trackPostDiscounts(voucher, isCart, isDiscount)
        dispatch(setOrderTotals(normalizedVouchersResponse.orderTotals))
        dispatch(postVouchersSuccess(normalizedVouchersResponse))
        dispatch(setCartItemPrices(normalizedVouchersResponse))
        dispatch(setPostingVoucherWithCode(''))
      }
    } catch (error) {
      const unAppliedError = error.response?.data?.unAppliedVouchers?.[0]
      let fallbackError
      if (!unAppliedError && error.response?.status === 400) {
        fallbackError = {
          code: '1006',
          message: 'voucher_invalid',
          target: 'couponCode',
          type: 'error',
        }
      }
      if (!isCheckout || (isCheckout && error?.response?.status !== 400)) {
        trackPostDiscountsError(unAppliedError || fallbackError || error, isCart, isCheckout)
      }
      dispatch(postVouchersFail(unAppliedError || fallbackError || getErrorData(error)))
      dispatch(setPostingVoucherWithCode(''))
    }
  }

export const operationClearDiscountError = (): ThunkResult<Promise<void>> => async (dispatch) => {
  dispatch(clearErrors())
}

export const operationClearOfferError = (): ThunkResult<Promise<void>> => async (dispatch) => {
  dispatch(clearErrors())
}

export const operationsToggleVoucher =
  (voucher: Voucher | Offer, location?: string): ThunkResult<Promise<void>> =>
  async (dispatch, getState) => {
    const state = getState()
    const isCheckout = location === VOUCHERS_LOCATION.checkout
    const offers = selectors.getOffers(state)
    const appliedVouchers = selectors.getAppliedVouchers(state)
    const configuredOffers = selectors.getConfiguredOffers(state)
    const isAppliedVoucher: boolean = !!appliedVouchers.find(
      (appliedVoucher: Voucher | Offer): boolean =>
        appliedVoucher.offerPropositionId === voucher.offerPropositionId,
    )
    const selectedOffer: Offer | undefined = offers.find(
      (offer: Offer): boolean => offer.offerPropositionId === voucher.offerPropositionId,
    )
    const offerName = selectedOffer
      ? getExtendedOfferInfo(selectedOffer, configuredOffers)?.headline
      : ''

    if (isAppliedVoucher) {
      if (isCheckout && offerName) {
        dispatch(
          trackCOEvent({
            event: CHECKOUT_TRACKING_EVENTS.removeVoucherClick.id,
            add: {
              voucher_name: offerName,
              dep_variable_name: ['voucher_name'],
              dep_variable_format: ['string'],
            },
          }),
        )
      }
      dispatch(operationDeleteVouchers(voucher.voucherCode, location))
    } else {
      if (isCheckout && offerName) {
        dispatch(
          trackCOEvent({
            event: CHECKOUT_TRACKING_EVENTS.addVoucherClick.id,
            add: {
              voucher_name: offerName,
              dep_variable_name: ['voucher_name'],
              dep_variable_format: ['string'],
            },
          }),
        )
      }
      dispatch(operationPostVouchers(selectedOffer || voucher, location))
    }
    dispatch(setLastAppliedUnavailableVoucher(undefined))
  }

export const operationClearDeletedDiscounts =
  (): ThunkResult<Promise<void>> => async (dispatch) => {
    dispatch(clearStoredDeletedVouchers())
  }
